import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { archiveNotification, getIntelligenceOverview, getMorningBrief, getProjectIntelligenceOverview, markNotificationRead, runIntelligenceSkill } from "@/services/intelligence";
import { AionAppShell } from "@/components/aion/AionAppShell";
import { UniversalActionBar } from "@/components/aion/UniversalActionBar";
import { HomeView } from "@/components/aion/HomeView";
import { BriefingView } from "@/components/aion/BriefingView";
import { BrainView } from "@/components/aion/BrainView";
import { AionView } from "@/components/aion/AionView";
import { TodayView } from "@/components/aion/TodayView";
import { MemoryView } from "@/components/aion/MemoryView";
import { ConversationView } from "@/components/aion/ConversationView";
import { NotificationsView } from "@/components/aion/NotificationsView";
import { ProjectsView } from "@/components/aion/ProjectsView";
import { DecisionsView } from "@/components/aion/DecisionsView";
import { AionVoiceSettings } from "@/components/aion/AionVoiceSettings";
import { MoreView } from "@/components/aion/MoreView";
import { useAionVoiceWorkspace } from "@/hooks/useAionVoiceWorkspace";
import { connectWS } from "@/services/websocket";
import { shouldConnectRealtime } from "@/lib/previewRuntime";
import {
  getAionDeviceSessions,
  getAionDevices,
  getAionDevicesOverview,
  getPlatformDashboard,
  getPlatformCustomers,
  getPlatformEmployees,
  getPlatformHome,
  getPlatformHardware,
  getPlatformInstallations,
  getPlatformRoles,
  getPlatformSystemSummary,
  getPlatformUsers,
  getFamilyProfiles,
  getAgents,
  getConsciousness,
  getConversationHistory,
  getDecisionLog,
  getHealth,
  getHardwareConnectors,
  getHardwareDevices,
  getLocalCoreStatus,
  getMemory,
  getMemoryTopics,
  getObservabilityOverview,
  getPredictions,
  getRooms,
  getSuggestions,
  getSystemStatus,
  getTrustCenter,
  searchMemory,
  switchFamilyProfile,
  updateMemoryPreferences,
} from "@/services/api";
import { getAgentLogs } from "@/services/agents";
import { getDevices } from "@/services/devices";
import { getEnergyDevices, getEnergyForecast, getEnergyStatus, optimizeEnergy } from "@/services/energy";
import { getScenes } from "@/services/scenes";
import { getMarketIntelligenceStatus, getMarketIntelligenceSummary, getMarketLearningSummary } from "@/services/marketIntelligence";

const REFRESH_INTERVAL = 8000;
const LIGHT_REFRESH_EVENT_TYPES = new Set([
  "voice_response_generated",
  "memory_created",
  "memory_updated",
  "summary_generated",
]);

const REFRESH_EVENT_TYPES = new Set([
  "device_executed",
  "scene_started",
  "scene_completed",
  "energy_updated",
  "energy_total_updated",
  "energy_spike_detected",
  "morning_brief_ready",
  "connections_ready",
  "patterns_ready",
  "decision_review_ready",
  "intelligence_notification",
]);
const PRIVATE_HEALTH_VOICE_BUSY_STATES = new Set([
  "SPEECH_DETECTED",
  "TRANSCRIBING",
  "THINKING",
  "EXECUTING",
  "RESPONDING",
  "SPEAKING",
  "INTERRUPTED",
]);

const REALTIME_VOICE_LOG_PRIVATE_KEYS = new Set([
  "response",
  "transcript",
  "partial_transcript",
  "audio_base64",
  "audio_url",
  "ready_tts_chunk",
  "text",
  "message",
]);

const redactRealtimeVoiceLogValue = (value) => {
  if (Array.isArray(value)) return value.map(redactRealtimeVoiceLogValue);
  if (!value || typeof value !== "object") return value;
  return Object.fromEntries(
    Object.entries(value)
      .filter(([key]) => !REALTIME_VOICE_LOG_PRIVATE_KEYS.has(key))
      .map(([key, item]) => [key, redactRealtimeVoiceLogValue(item)]),
  );
};


const buildGroupedDevices = (rooms = [], devices = []) => ({
  rooms: rooms.map((room) => ({
    ...room,
    devices: devices.filter((device) => device.room_id === room.id),
  })),
});

const buildCompactHomeStatus = (rooms = [], devices = [], energyStatus) => {
  const activeDevices = devices.filter((device) => device.state?.power === "on");
  const firstActive = activeDevices[0];
  const roomMap = Object.fromEntries(rooms.map((room) => [room.id, room.name]));
  const rawPower = energyStatus?.current_power;
  const currentPowerValue = rawPower == null || rawPower === "" ? NaN : Number(rawPower);
  return {
    activeDevices: activeDevices.length,
    deviceCount: devices.length,
    roomCount: rooms.length,
    currentPower: Number.isFinite(currentPowerValue) ? `${currentPowerValue} W` : "—",
    currentPowerValue: Number.isFinite(currentPowerValue) ? currentPowerValue : null,
    headline: firstActive ? `${roomMap[firstActive.room_id] || "Zuhause"}: ${firstActive.name} ist aktiv.` : "Keine wichtigen Geräte laufen gerade aktiv.",
  };
};

const buildHumanStatus = ({ events, intelligence, voicePending, voiceDisplayState }) => {
  if (voicePending) return "AION formuliert gerade eine Antwort.";
  if (voiceDisplayState === "HÖRT ZU") return "AION hört gerade aktiv zu.";
  if (voiceDisplayState === "SPRICHT") return "AION antwortet dir gerade per Stimme.";
  const eventType = events[0]?.data?.type || events[0]?.type || events[0]?.event_type;
  if (eventType === "memory_updated" || eventType === "summary_generated") return "AION aktualisiert deine Erinnerungen.";
  if (eventType === "connections_ready") return "AION hat neue Zusammenhänge erkannt.";
  if (eventType === "patterns_ready") return "AION überprüft gerade Muster und Risiken.";
  const notifications = intelligence?.notifications || [];
  const unreadNotifications = notifications.filter((item) => !item?.read);
  if (unreadNotifications.length) return `${unreadNotifications.length} neue Hinweise sind für dich bereit.`;
  const opportunities = intelligence?.home?.opportunities || [];
  if (opportunities.length) return `AION hat ${opportunities.length} neue Chancen erkannt.`;
  return "Keine wichtigen Aktionen laufen.";
};

const localizedProfileLabel = (profile) => {
  if (!profile) return "Mein Bereich";
  return profile.access_mode === "shared_view" ? `${profile.name} · Gemeinsam` : `${profile.name} · Privat`;
};

const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));
const DASHBOARD_VIEWS = new Set(["aion", "home", "brain", "briefing", "conversations", "today", "notifications", "decisions", "memory", "projects", "more"]);

const resolveDashboardViewFromHash = () => {
  if (typeof window === "undefined") return null;
  const raw = String(window.location.hash || "").replace(/^#\/?/, "").trim().toLowerCase();
  return DASHBOARD_VIEWS.has(raw) ? raw : null;
};

export default function Dashboard({ token, initialUser, onLogout }) {
  const startsInAdminWorkspace = initialUser?.user_type === "platform_employee";
  const [activeView, setActiveView] = useState(() => resolveDashboardViewFromHash() || (startsInAdminWorkspace ? "more" : "home"));
  const [moreSection, setMoreSection] = useState(() => startsInAdminWorkspace ? "admin" : "devices");
  const [events, setEvents] = useState([]);
  const [memorySearchQuery, setMemorySearchQuery] = useState("");
  const [memorySearchResult, setMemorySearchResult] = useState(null);
  const [selectedMemoryItem, setSelectedMemoryItem] = useState(null);
  const [focusedDecisionId, setFocusedDecisionId] = useState(null);
  const [focusedProjectId, setFocusedProjectId] = useState(null);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [profileSwitchPending, setProfileSwitchPending] = useState(false);
  const [uiError, setUiError] = useState("");
  const [pendingPrivateHealthVoice, setPendingPrivateHealthVoice] = useState(null);
  const isTrustCenterView = activeView === "more" && moreSection === "privacy";
  const activeViewRef = useRef(activeView);
  const websocketMessageHandlerRef = useRef(null);
  const voiceContextRefreshTimerRef = useRef(null);
  const memorySearchGenerationRef = useRef(0);

  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  const applyActiveView = useCallback((nextView, { syncHash = true } = {}) => {
    if (!DASHBOARD_VIEWS.has(nextView)) return;
    setActiveView((previous) => (previous === nextView ? previous : nextView));
    if (!syncHash || typeof window === "undefined") return;
    const nextHash = `#/${nextView}`;
    if (window.location.hash !== nextHash) {
      window.history.replaceState(null, "", nextHash);
    }
  }, []);

  const familyQuery = useQuery({
    queryKey: ["aion-family-profiles", token],
    queryFn: () => getFamilyProfiles(token),
    refetchInterval: isTrustCenterView ? false : REFRESH_INTERVAL,
    retry: false,
  });

  useEffect(() => {
    const activeProfileUserId = familyQuery.data?.active_profile?.user_id;
    if (!activeProfileUserId) return;
    setSelectedProfileId((previous) => previous || activeProfileUserId);
  }, [familyQuery.data?.active_profile?.user_id]);

  const coreQuery = useQuery({
    queryKey: ["aion-core-experience", token, selectedProfileId],
    queryFn: async () => {
      const systemStatus = await getSystemStatus(token);
      const userId = systemStatus.user.id;
      const profileUserId = selectedProfileId || userId;
      const ownerMemoryPromise = getMemory(userId, token);
      const loadProfileMemory = async () => {
        if (profileUserId === userId) return ownerMemoryPromise;
        try {
          return await getMemory(profileUserId, token);
        } catch (error) {
          if (error?.status === 403) {
            return null;
          }
          throw error;
        }
      };
      const [rooms, devicesList, scenes, voiceMemory, memory, suggestions, intelligenceOverview, projectIntelligenceOverview, memoryTopics, conversationHistory, morningBrief, energyStatus, energyDevices, energyForecast] = await Promise.all([
        getRooms(token),
        getDevices(token),
        getScenes(token),
        ownerMemoryPromise,
        loadProfileMemory(),
        profileUserId === userId ? getSuggestions(userId, token) : Promise.resolve({ suggestions: [] }),
        getIntelligenceOverview(token, profileUserId),
        getProjectIntelligenceOverview(token).catch(() => null),
        getMemoryTopics(token, profileUserId),
        getConversationHistory(token, profileUserId),
        getMorningBrief(token, profileUserId),
        getEnergyStatus(token).catch(() => null),
        getEnergyDevices(token).catch(() => ({ devices: [] })),
        getEnergyForecast(token).catch(() => null),
      ]);
      return { systemStatus, rooms, devicesList, scenes, voiceMemory, memory, suggestions, intelligenceOverview, projectIntelligenceOverview, memoryTopics, conversationHistory, morningBrief, energyStatus, energyDevices, energyForecast, profileUserId };
    },
    refetchInterval: isTrustCenterView ? false : REFRESH_INTERVAL,
    retry: false,
  });

  const [trustCenter, setTrustCenter] = useState(null);
  const [trustCenterStatus, setTrustCenterStatus] = useState("IDLE");
  const [trustCenterError, setTrustCenterError] = useState("");

  const loadTrustCenter = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setTrustCenterStatus("LOADING");
    setTrustCenterError("");
    let lastError = null;
    for (const delay of [0, 1200]) {
      if (delay) await wait(delay);
      try {
        const snapshot = await getTrustCenter(token);
        setTrustCenter(snapshot);
        setTrustCenterStatus(snapshot ? "READY" : "EMPTY");
        return;
      } catch (error) {
        lastError = error;
        if (![408, 429].includes(error?.status)) break;
      }
    }
    setTrustCenter(null);
    setTrustCenterStatus("ERROR");
    setTrustCenterError(String(lastError?.message || "Trust Center konnte nicht geladen werden."));
  }, [token]);

  useEffect(() => {
    if (!(activeView === "home" || isTrustCenterView)) return;
    const timer = window.setTimeout(() => {
      void loadTrustCenter({ silent: activeView === "home" });
    }, 250);
    const interval = window.setInterval(() => {
      void loadTrustCenter({ silent: true });
    }, REFRESH_INTERVAL);
    return () => {
      window.clearTimeout(timer);
      window.clearInterval(interval);
    };
  }, [activeView, isTrustCenterView, loadTrustCenter]);

  const user = coreQuery.data?.systemStatus?.user || initialUser || null;
  const activeProfile = familyQuery.data?.profiles?.find((profile) => profile.user_id === (selectedProfileId || user?.id)) || familyQuery.data?.active_profile || null;
  const normalizedRole = String(user?.role || "").toLowerCase();
  const operatorRole = String(user?.account_role || (normalizedRole === "owner" ? "CUSTOMER_OWNER" : normalizedRole === "admin" ? "CUSTOMER_ADMIN" : user?.role || "USER")).toUpperCase();
  const isPlatformOperator = user?.user_type === "platform_employee" || ["PLATFORM_ADMIN", "MANAGER", "SALES_EMPLOYEE", "INSTALLER", "TECHNICIAN", "SUPPORT"].includes(operatorRole);
  const isAdmin = normalizedRole === "admin" || normalizedRole === "owner" || isPlatformOperator;

  const marketIntelligenceQuery = useQuery({
    enabled: Boolean(activeView === "more" && moreSection === "market"),
    queryKey: ["aion-market-intelligence", token],
    queryFn: async () => {
      const [status, summary, learning] = await Promise.all([
        getMarketIntelligenceStatus(token),
        getMarketIntelligenceSummary(token),
        getMarketLearningSummary(token),
      ]);
      return { status, summary, learning };
    },
    refetchInterval: activeView === "more" && moreSection === "market" ? REFRESH_INTERVAL : false,
    retry: false,
  });

  const adminQuery = useQuery({
    enabled: Boolean(isAdmin && activeView === "more" && moreSection !== "privacy"),
    queryKey: ["aion-admin-experience", token],
    queryFn: async () => {
      const [health, consciousness, agents, agentLogs, predictions, decisionLog, observability, localCore, hardwareDevices, hardwareConnectors, aionDevicesOverview, aionDevices, aionDeviceSessions, platformDashboard, platformCustomers, platformEmployees, platformHome, platformInstallations, platformHardware, platformUsers, platformRoles, platformSystemSummary] = await Promise.all([
        getHealth(token),
        getConsciousness(token),
        getAgents(token),
        getAgentLogs(token),
        getPredictions(token),
        getDecisionLog(token),
        getObservabilityOverview(token),
        getLocalCoreStatus(token),
        getHardwareDevices(token),
        getHardwareConnectors(token),
        getAionDevicesOverview(token),
        getAionDevices(token),
        getAionDeviceSessions(token),
        getPlatformDashboard(token),
        getPlatformCustomers(token),
        getPlatformEmployees(token).catch(() => []),
        getPlatformHome(token).catch(() => null),
        getPlatformInstallations(token),
        getPlatformHardware(token),
        getPlatformUsers(token),
        getPlatformRoles(token),
        getPlatformSystemSummary(token),
      ]);
      return { health, consciousness, agents, agentLogs, predictions, decisionLog, observability, localCore, hardwareDevices, hardwareConnectors, aionDevicesOverview, aionDevices, aionDeviceSessions, platformDashboard, platformCustomers, platformEmployees, platformHome, platformInstallations, platformHardware, platformUsers, platformRoles, platformSystemSummary, events: [] };
    },
    refetchInterval: isTrustCenterView ? false : REFRESH_INTERVAL,
    retry: false,
  });

  useEffect(() => {
    // Do not destroy a valid browser session because one optional dashboard module
    // returns 401 while the authenticated /auth/me session is still valid.
    // Session validity is owned by App's cookie restore and explicit logout.
    const unauthorized = [coreQuery.error, familyQuery.error].find((error) => error?.status === 401);
    if (unauthorized && !initialUser?.id) {
      onLogout("Sitzung abgelaufen. Bitte erneut anmelden.");
    }
  }, [coreQuery.error, familyQuery.error, initialUser?.id, onLogout]);

  const refreshAll = useCallback(async () => {
    await familyQuery.refetch();
    await coreQuery.refetch();
    if (isAdmin && activeView === "more" && moreSection !== "privacy") await adminQuery.refetch();
    await loadTrustCenter();
  }, [activeView, adminQuery, coreQuery, familyQuery, isAdmin, loadTrustCenter, moreSection]);

  const refreshVoiceContext = useCallback(async () => {
    await coreQuery.refetch();
  }, [coreQuery]);

  const scheduleVoiceContextRefresh = useCallback(() => {
    if (voiceContextRefreshTimerRef.current) {
      window.clearTimeout(voiceContextRefreshTimerRef.current);
    }
    voiceContextRefreshTimerRef.current = window.setTimeout(() => {
      voiceContextRefreshTimerRef.current = null;
      void refreshVoiceContext();
    }, 120);
  }, [refreshVoiceContext]);

  useEffect(() => () => {
    if (voiceContextRefreshTimerRef.current) {
      window.clearTimeout(voiceContextRefreshTimerRef.current);
      voiceContextRefreshTimerRef.current = null;
    }
  }, []);

  const voice = useAionVoiceWorkspace({ token, memory: coreQuery.data?.voiceMemory || coreQuery.data?.memory, onRefresh: refreshVoiceContext, activeProfileUserId: selectedProfileId || initialUser?.id, trustPrivacyKey: JSON.stringify(trustCenter?.privacy || {}) });
  const {
    handleRealtimeVoiceEvent,
    handsFreeMicEnabled,
    selectedVoiceLanguages,
    voiceMuted,
    voicePending,
    voiceRecording,
    voiceSessionId,
    voiceState,
    voiceStatus,
  } = voice;

  const handleWebsocketMessage = useCallback((message) => {
    const eventType = message?.data?.type || message?.type || message?.event_type;
    const payload = message?.data?.payload || message?.payload || message?.data || {};
    const lockedPrivateHealthEvent = (
      eventType === "intelligence_notification"
      && payload?.action === "OPEN_PERSONAL_HEALTH"
      && payload?.privacy_scope === "PERSONAL_USER_ONLY"
      && payload?.requires_unlock === true
      && payload?.opens_automatically === false
    );
    const voiceLogEvent = typeof eventType === "string" && eventType.startsWith("voice_");
    const logSafeMessage = lockedPrivateHealthEvent
      ? {
        type: eventType,
        payload: {
          action: "OPEN_PERSONAL_HEALTH",
          privacy_scope: "PERSONAL_USER_ONLY",
          requires_unlock: true,
          opens_automatically: false,
          content_locked: true,
        },
      }
      : voiceLogEvent
        ? { type: eventType, payload: redactRealtimeVoiceLogValue(payload) }
        : message;
    setEvents((prev) => [logSafeMessage, ...prev].slice(0, 50));
    handleRealtimeVoiceEvent?.(message);
    if (lockedPrivateHealthEvent) {
      // Never speak the private health payload before explicit unlock.
      setPendingPrivateHealthVoice({
        message: "Ein privater Gesundheits-Hinweis ist verfügbar. Öffne Personal Health und entsperre den Bereich.",
      });
    }
    if (eventType && LIGHT_REFRESH_EVENT_TYPES.has(eventType)) {
      scheduleVoiceContextRefresh();
    } else if (eventType && REFRESH_EVENT_TYPES.has(eventType)) {
      void refreshAll();
    }
  }, [handleRealtimeVoiceEvent, refreshAll, scheduleVoiceContextRefresh]);

  useEffect(() => {
    const pending = pendingPrivateHealthVoice;
    if (!pending) return;
    const ownerUserId = String(initialUser?.id || user?.id || "");
    const activeProfileUserId = String(selectedProfileId || user?.id || initialUser?.id || "");
    const policyBlocked = Boolean(voiceStatus?.privacy?.policy_blocked);
    const busyState = PRIVATE_HEALTH_VOICE_BUSY_STATES.has(String(voiceState || "").toUpperCase());

    if (!ownerUserId || activeProfileUserId !== ownerUserId || voiceMuted || policyBlocked || !handsFreeMicEnabled) {
      setPendingPrivateHealthVoice(null);
      return;
    }
    if (voicePending || voiceRecording || busyState) return;

    setPendingPrivateHealthVoice(null);
    const language = selectedVoiceLanguages?.[0] || "de";
    handleRealtimeVoiceEvent?.({
      data: {
        type: "voice_response_partial",
        payload: {
          session_id: voiceSessionId,
          response: pending.message,
          language,
          route_classification: "PRIVATE_HEALTH_CONTEXT",
          ready_tts_chunk: pending.message,
          chunk_boundary: Number.MAX_SAFE_INTEGER,
          is_final: true,
        },
      },
    });
  }, [handleRealtimeVoiceEvent, handsFreeMicEnabled, initialUser?.id, pendingPrivateHealthVoice, selectedProfileId, selectedVoiceLanguages, user?.id, voiceMuted, voicePending, voiceRecording, voiceSessionId, voiceState, voiceStatus?.privacy?.policy_blocked]);

  websocketMessageHandlerRef.current = handleWebsocketMessage;

  useEffect(() => {
    const hostname = typeof window === "undefined" ? "" : window.location.hostname;
    if (!shouldConnectRealtime({ hostname, isTrustCenterView })) return undefined;
    const ws = connectWS(token, (message) => websocketMessageHandlerRef.current?.(message));
    return () => ws.close();
  }, [isTrustCenterView, token]);

  const status = coreQuery.data?.systemStatus || null;
  const rooms = useMemo(() => coreQuery.data?.rooms || [], [coreQuery.data?.rooms]);
  const devicesList = useMemo(() => coreQuery.data?.devicesList || [], [coreQuery.data?.devicesList]);
  const groupedDevices = useMemo(() => buildGroupedDevices(rooms, devicesList), [rooms, devicesList]);
  const scenes = coreQuery.data?.scenes || [];
  const intelligence = coreQuery.data?.intelligenceOverview || null;
  const memory = coreQuery.data?.memory || intelligence?.memory || null;
  const memoryTopics = coreQuery.data?.memoryTopics || { topics: [], personal_context: {} };
  const suggestions = coreQuery.data?.suggestions?.suggestions || [];
  const morningBrief = coreQuery.data?.morningBrief || intelligence?.morning_brief || null;
  const energyStatus = coreQuery.data?.energyStatus || null;
  const energyDevices = coreQuery.data?.energyDevices || { devices: [] };
  const energyForecast = coreQuery.data?.energyForecast || null;
  const home = intelligence?.home || {};
  const compactHomeStatus = useMemo(() => buildCompactHomeStatus(rooms, devicesList, energyStatus), [devicesList, energyStatus, rooms]);
  const humanStatus = useMemo(() => buildHumanStatus({ events, intelligence, voicePending: voice.voicePending, voiceDisplayState: voice.voiceDisplayState }), [events, intelligence, voice.voiceDisplayState, voice.voicePending]);
  const adminData = useMemo(() => ({ ...(adminQuery.data || {}), events }), [adminQuery.data, events]);
  const visibleQueryError = [coreQuery.error, familyQuery.error, isAdmin ? adminQuery.error : null]
    .find((queryError) => queryError && queryError.status !== 401);
  const error = uiError || visibleQueryError?.message || "";

  const handleOptimizeEnergy = async () => {
    await optimizeEnergy(token);
    await refreshAll();
  };

  const handleMemorySearch = async () => {
    const requestGeneration = memorySearchGenerationRef.current + 1;
    memorySearchGenerationRef.current = requestGeneration;
    const result = await searchMemory({ query: memorySearchQuery, limit: 8, profile_user_id: selectedProfileId || user?.id }, token);
    if (requestGeneration !== memorySearchGenerationRef.current) return;
    setMemorySearchResult(result);
  };

  const handleProfileSwitch = async (targetUserId) => {
    setProfileSwitchPending(true);
    setUiError("");
    try {
      await switchFamilyProfile(targetUserId, token);
      setSelectedProfileId(targetUserId);
      memorySearchGenerationRef.current += 1;
      setMemorySearchResult(null);
      setSelectedMemoryItem(null);
      setFocusedDecisionId(null);
      setFocusedProjectId(null);
      setPendingPrivateHealthVoice(null);
      // The core query key includes selectedProfileId and will load the new profile
      // after this state change. Only refresh family metadata here to avoid a stale
      // old-profile core refetch before React commits the new profile selection.
      await familyQuery.refetch();
    } catch (switchError) {
      setUiError(switchError?.message || "Profil konnte nicht gewechselt werden.");
    } finally {
      setProfileSwitchPending(false);
    }
  };

  const handleSaveMemoryPreferences = async (payload) => {
    if (activeProfile?.access_mode === "shared_view") {
      setUiError("Persönliche Memory-Einstellungen können nur im eigenen Profil geändert werden.");
      return;
    }
    try {
      await updateMemoryPreferences({
        favorite_devices: payload.favorite_devices || memory?.favorite_devices || [],
        favorite_scenes: payload.favorite_scenes || memory?.favorite_scenes || [],
        personalized_automations: payload.personalized_automations || memory?.personalized_automations || [],
        // General settings intentionally omit language and assistant profile fields.
        // The backend preserves omitted preferences so Voice Settings remain authoritative.
      }, token);
      await refreshAll();
      setUiError("");
    } catch (saveError) {
      setUiError(saveError?.message || "Memory update failed");
    }
  };

  const handleNotificationRead = async (notificationId) => {
    setUiError("");
    try {
      await markNotificationRead(notificationId, token);
      await refreshAll();
    } catch (notificationError) {
      setUiError(notificationError?.message || "Hinweis konnte nicht als gelesen markiert werden.");
    }
  };

  const handleNotificationArchive = async (notificationId) => {
    setUiError("");
    try {
      await archiveNotification(notificationId, token);
      await refreshAll();
    } catch (notificationError) {
      setUiError(notificationError?.message || "Hinweis konnte nicht archiviert werden.");
    }
  };

  const handleRunMorningBrief = async () => {
    if (activeProfile?.access_mode === "shared_view") {
      setUiError("Der Morning Brief kann nur im eigenen Profil neu erstellt werden.");
      return;
    }
    await runIntelligenceSkill("morning_brief", token);
    await refreshAll();
  };

  const handleGoToMoreSection = useCallback((section = "devices") => {
    const allowedSections = new Set(["devices", "spatial", "health", "vehicle", "scenes", "automations", "cameras", "robotics", "communication", "appointments", "tasks", "projects", "energy", "settings", "system", "privacy"]);
    if (isAdmin) {
      allowedSections.add("admin");
      allowedSections.add("advanced");
    }
    const nextSection = allowedSections.has(section) ? section : "devices";
    setMoreSection(nextSection);
    applyActiveView("more");
    if (nextSection === "privacy") void loadTrustCenter();
  }, [applyActiveView, isAdmin, loadTrustCenter]);

  const handleGoToHomeDevices = () => handleGoToMoreSection("devices");

  const handleOpenDecisionReview = useCallback((decisionId = null) => {
    setFocusedDecisionId(decisionId || null);
    applyActiveView("decisions");
  }, [applyActiveView]);

  const handleOpenProjectWorkspace = useCallback((projectId = null) => {
    setFocusedProjectId(projectId || null);
    applyActiveView("projects");
  }, [applyActiveView]);

  const handleOpenPersonalHealth = () => {
    setMoreSection("health");
    applyActiveView("more");
  };

  const handleGoToTrustCenter = () => handleGoToMoreSection("privacy");

  const handleExamplePrompt = async (prompt) => {
    setUiError("");
    voice.setVoiceCommand(prompt);
    await voice.runConversation(prompt, { source: "text" });
  };

  const fixedActionBarVisible = activeView === "aion" || activeView === "home";

  useEffect(() => {
    const syncFromHash = () => {
      const nextView = resolveDashboardViewFromHash();
      if (nextView && nextView !== activeViewRef.current) {
        applyActiveView(nextView, { syncHash: false });
      }
    };
    window.addEventListener("hashchange", syncFromHash);
    syncFromHash();
    return () => window.removeEventListener("hashchange", syncFromHash);
  }, [applyActiveView]);

  return (
    <AionAppShell
      activeView={activeView}
      activeSection={moreSection}
      onViewChange={applyActiveView}
      onMoreSectionChange={handleGoToMoreSection}
      activeProfile={activeProfile}
      familyProfiles={familyQuery.data?.profiles || []}
      onProfileSwitch={handleProfileSwitch}
      profileSwitchPending={profileSwitchPending}
      user={user}
      navBadges={{
        notifications: (intelligence?.notifications || []).filter((item) => !item?.read).length,
        decisions: (intelligence?.decisions || []).filter((item) => ["open", "warning", "needs_review"].includes(item?.review_status)).length,
        projects: (intelligence?.planning?.projects || intelligence?.home?.active_projects || []).filter((item) => ["blocked", "at_risk"].includes(item?.status) || item?.priority === "critical").length,
        today: (home?.open_tasks?.length || 0)
          + (home?.open_decisions?.length || 0)
          + (intelligence?.notifications || []).filter((item) => !item?.read).length,
      }}
      onLogout={onLogout}
      actionBar={fixedActionBarVisible ? (
        <UniversalActionBar
          actionLabel="Was soll AION für dich tun?"
          variant="full"
          placement="fixed"
          audioFileInputRef={voice.audioFileInputRef}
          onAudioFileSelected={voice.handleAudioFileSelected}
          onOpenAudioPicker={voice.openAudioFilePicker}
          onSubmit={voice.handleVoiceSubmit}
          onStopOutput={voice.stopSpeechOutput}
          onVoiceCommandChange={voice.setVoiceCommand}
          onVoiceRecording={voice.toggleAlwaysListening}
          pending={voice.voicePending}
          recording={voice.voiceRecording || voice.handsFreeMicEnabled}
          value={voice.voiceCommand}
          voiceDisplayState={voice.voiceDisplayState}
          voiceInputMode={voice.voiceInputMode}
          voiceState={voice.voiceState}
          voiceCaptureSupported={voice.voiceCaptureSupported}
          voiceMicrophonePolicyBlocked={Boolean(voice.voiceStatus?.privacy?.policy_blocked)}
        />
      ) : null}
    >
      {error ? <div className="aion-error-box" data-testid="dashboard-error-box">{error}</div> : null}

      {activeView === "home" ? (
        <HomeView
          home={home}
          projectIntelligence={coreQuery.data?.projectIntelligenceOverview || null}
          userName={activeProfile?.name || user?.name || ""}
          registeredDevices={devicesList}
          scenes={scenes}
          assistantVoiceProfile={voice.assistantVoiceProfile}
          onVoiceProfileSave={voice.handleAssistantVoiceProfileSave}
          voiceStatus={voice.voiceStatus}
          voicePending={voice.voicePending}
          compactHomeStatus={compactHomeStatus}
          localCoreStatus={adminData?.localCore}
          compactActionBar={(
            <UniversalActionBar
              actionLabel="AION Schnellzugriff"
              variant="compact"
              placement="inline"
              audioFileInputRef={voice.audioFileInputRef}
              onAudioFileSelected={voice.handleAudioFileSelected}
              onOpenAudioPicker={voice.openAudioFilePicker}
              onSubmit={voice.handleVoiceSubmit}
              onStopOutput={voice.stopSpeechOutput}
              onVoiceCommandChange={voice.setVoiceCommand}
              onVoiceRecording={voice.toggleAlwaysListening}
              pending={voice.voicePending}
              recording={voice.voiceRecording || voice.handsFreeMicEnabled}
              value={voice.voiceCommand}
              voiceDisplayState={voice.voiceDisplayState}
              voiceInputMode={voice.voiceInputMode}
              voiceState={voice.voiceState}
              voiceCaptureSupported={voice.voiceCaptureSupported}
              voiceMicrophonePolicyBlocked={Boolean(voice.voiceStatus?.privacy?.policy_blocked)}
            />
          )}
          onGoToConversations={() => applyActiveView("conversations")}
          onGoToDecisions={() => handleOpenDecisionReview()}
          onGoToMore={handleGoToHomeDevices}
          onGoToNotifications={() => applyActiveView("notifications")}
          onGoToProjects={() => handleOpenProjectWorkspace()}
          onOpenMoreSection={handleGoToMoreSection}
          onGoToToday={() => applyActiveView("today")}
          onGoToMemory={() => applyActiveView("memory")}
          onGoToTrustCenter={handleGoToTrustCenter}
          trustHomeCard={trustCenter?.home_card}
          voiceDisplay={voice.voiceDisplay}
          voiceDisplayState={voice.voiceDisplayState}
          voicePartialTranscript={voice.voicePartialTranscript}
          voiceState={voice.voiceState}
          wakeWordSupported={voice.wakeWordSupported}
          voiceCaptureSupported={voice.voiceCaptureSupported}
          audioInputLevel={voice.audioInputLevel}
          audioOutputLevel={voice.audioOutputLevel}
        />
      ) : null}

      {activeView === "aion" ? (
        <AionView
          assistantVoiceProfile={voice.assistantVoiceProfile}
          audioInputLevel={voice.audioInputLevel}
          handsFreeMicEnabled={voice.handsFreeMicEnabled}
          audioOutputLevel={voice.audioOutputLevel}
          memory={memory}
          memoryRecallResult={voice.memoryRecallResult}
          memoryTopics={memoryTopics}
          onExamplePrompt={handleExamplePrompt}
          onLanguageSave={voice.handleVoiceLanguagesSave}
          onStartWakeWord={voice.startWakeWordMode}
          onToggleHandsFree={voice.toggleAlwaysListening}
          onStopVoiceOutput={voice.stopSpeechOutput}
          onStopWakeWord={voice.stopWakeWordMode}
          onToggleMute={voice.toggleVoiceMute}
          onToggleLanguage={voice.handleVoiceLanguageToggle}
          onVoiceProfileSave={voice.handleAssistantVoiceProfileSave}
          selectedVoiceLanguages={voice.selectedVoiceLanguages}
          voiceDisplay={voice.voiceDisplay}
          voiceError={voice.voiceError}
          voiceLanguageMode={voice.voiceLanguageMode}
          voiceLanguageModeLabel={voice.voiceLanguageModeLabel}
          voiceMuted={voice.voiceMuted}
          voicePending={voice.voicePending}
          voicePartialTranscript={voice.voicePartialTranscript}
          voiceResearch={voice.voiceResult?.research}
          voiceSessionId={voice.voiceSessionId}
          voiceStatus={voice.voiceStatus}
          vadSupported={voice.vadSupported}
          wakeWordArmed={voice.wakeWordArmed}
          wakeWordHeard={voice.wakeWordHeard}
          wakeWordSupported={voice.wakeWordSupported}
          voiceCaptureSupported={voice.voiceCaptureSupported}
        />
      ) : null}

      {activeView === "brain" ? (
        <BrainView token={token} activeProfile={activeProfile} />
      ) : null}

      {activeView === "briefing" ? (
        <BriefingView token={token} />
      ) : null}

      {activeView === "conversations" ? (
        <ConversationView
          activeProfile={activeProfile}
          conversationHistory={coreQuery.data?.conversationHistory || []}
          onOpenMemory={() => applyActiveView("memory")}
        />
      ) : null}

      {activeView === "notifications" ? (
        <NotificationsView
          activeProfile={activeProfile}
          notifications={intelligence?.notifications || []}
          onArchiveNotification={activeProfile?.access_mode === "shared_view" ? undefined : handleNotificationArchive}
          onMarkNotificationRead={activeProfile?.access_mode === "shared_view" ? undefined : handleNotificationRead}
          onOpenDecisions={() => handleOpenDecisionReview()}
          onOpenPersonalHealth={activeProfile?.access_mode === "shared_view" ? undefined : handleOpenPersonalHealth}
          onOpenProjects={handleOpenProjectWorkspace}
        />
      ) : null}

      {activeView === "today" ? (
        <TodayView
          morningBrief={morningBrief}
          notifications={intelligence?.notifications || []}
          openTasks={home?.open_tasks || []}
          openDecisions={home?.open_decisions || []}
          recentMemories={home?.recent_memories || []}
          suggestions={suggestions}
          onArchiveNotification={activeProfile?.access_mode === "shared_view" ? undefined : handleNotificationArchive}
          onMarkNotificationRead={activeProfile?.access_mode === "shared_view" ? undefined : handleNotificationRead}
          onOpenDecisions={() => handleOpenDecisionReview()}
          onOpenNotifications={() => applyActiveView("notifications")}
          onOpenPersonalHealth={activeProfile?.access_mode === "shared_view" ? undefined : handleOpenPersonalHealth}
          onRunMorningBrief={handleRunMorningBrief}
          canRefreshMorningBrief={activeProfile?.access_mode !== "shared_view"}
        />
      ) : null}

      {activeView === "decisions" ? (
        <DecisionsView
          activeProfile={activeProfile}
          decisions={intelligence?.decisions || []}
          focusDecisionId={focusedDecisionId}
          onFocusConsumed={() => setFocusedDecisionId(null)}
          onOpenMemory={() => applyActiveView("memory")}
        />
      ) : null}

      {activeView === "projects" ? (
        <ProjectsView
          activeProfile={activeProfile}
          projects={intelligence?.planning?.projects || intelligence?.home?.active_projects || []}
          projectIntelligence={coreQuery.data?.projectIntelligenceOverview || null}
          focusProjectId={focusedProjectId}
          onFocusConsumed={() => setFocusedProjectId(null)}
          onOpenDecisions={handleOpenDecisionReview}
          onOpenMemory={() => applyActiveView("memory")}
        />
      ) : null}

      {activeView === "memory" ? (
        <MemoryView
          activeProfile={activeProfile}
          captures={intelligence?.captures || []}
          decisions={intelligence?.decisions || []}
          projects={intelligence?.planning?.projects || intelligence?.home?.active_projects || []}
          conversationHistory={coreQuery.data?.conversationHistory || []}
          memory={memory}
          memoryRecallResult={voice.memoryRecallResult}
          memorySearchQuery={memorySearchQuery}
          memorySearchResult={memorySearchResult}
          memoryTopics={memoryTopics}
          onMemorySearch={handleMemorySearch}
          onMemorySearchQueryChange={setMemorySearchQuery}
          onOpenConversations={() => applyActiveView("conversations")}
          onOpenDecisions={() => handleOpenDecisionReview()}
          onOpenProjects={() => handleOpenProjectWorkspace()}
          onSelectMemoryItem={setSelectedMemoryItem}
          selectedMemoryItem={selectedMemoryItem}
        />
      ) : null}

      {activeView === "more" ? (
        <MoreView
          activeSection={moreSection}
          voiceSettings={<AionVoiceSettings voice={voice} />}
          home={home}
          adminData={adminData}
          compactHomeStatus={compactHomeStatus}
          devices={groupedDevices}
          energyDevices={energyDevices}
          energyForecast={energyForecast}
          energyStatus={energyStatus}
          intelligence={intelligence}
          isAdmin={isAdmin}
          memory={memory}
          onOptimizeEnergy={handleOptimizeEnergy}
          onOpenProjects={() => handleOpenProjectWorkspace()}
          onRefresh={refreshAll}
          onSaveMemoryPreferences={handleSaveMemoryPreferences}
          onSectionChange={setMoreSection}
          scenes={scenes}
          status={status}
          token={token}
          profileLabel={localizedProfileLabel(activeProfile)}
          profileAccessMode={activeProfile?.access_mode || "self"}
          profileScopeKey={activeProfile?.user_id || activeProfile?.id || selectedProfileId || initialUser?.id || "self"}
          operatorRole={operatorRole}
          trustCenter={trustCenter}
          trustCenterStatus={trustCenterStatus}
          trustCenterError={trustCenterError}
          onRetryTrustCenter={() => loadTrustCenter()}
          voiceDiagnostics={voice.voiceDiagnostics}
        />
      ) : null}
    </AionAppShell>
  );
}
